import {
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { useSelector, useDispatch } from 'react-redux';
import { namespaces } from '../../../../../../i18n/i18n.constants';
import { ObjectType } from '../../../../../../types/objectType';
import {
  updateListsByFilter,
  toggleIsLoading,
} from '../../../../../../redux/modules/civilWorkWorkerList';
import { CombinedFilterCollapsableWrapper } from '../../Wrappers/CombinedFilterCollapsableWrapper/combinedFilterCollapsableWrapper';
import { CustomSelectMultiple } from '../CustomSelectMultiple/CustomSelectMultiple';
import { CustomSelect } from '../SelectFilter/CustomSelect/CustomSelect';
import { getTranslatedOptions } from '../../../../../../helpers/stringHelpers';
import { getUrlCombinedFilterParamsToStore } from '../../../../../../helpers/urlHelpers';
import {
  SELECT_OPTIONS_BY_FILTER,
  LIST_TYPES,
} from '../../../../../../config';
import { event } from '../../../../../../config/analyticsConfig';
import workWorkersAPI from '../../../../../../services/worksWorkersAPI';

const getAutocompleteValues = async (filterType: string) => {
  const searchText = '';
  try {
    const response = await workWorkersAPI.autocompleteServiceWorksWorkers(filterType, searchText || '');
    const { data } = response;
    if (response.status === 200) {
      const { results } = data;
      if (results && results.length > 0) {
        return data.results;
      }
    }
  } catch (error) {
    // console.log(error);
    return [];
  }
  return [];
};

interface SelectMultipleAndSelectProps {
  filterType: string;
  combinedFilterType: string;
  tooltipMessage?: string;
}

export const SelectMultipleAndSelect = (props: SelectMultipleAndSelectProps) => {
  const {
    filterType, combinedFilterType, tooltipMessage,
  } = props;

  const { t } = useTranslation(namespaces.pages.resumesSearch);
  const dispatch = useDispatch();
  const {
    tags: initialTags,
  } = useSelector((store: ObjectType) => store.civilWorksPersonLists);
  const [selectOptions, setSelectOptions] = useState<ObjectType>([]);
  const [selectedOptions, setSelectedOptions] = useState<Array<string | undefined>>([]);
  const [tags, setTags] = useState<Array<ObjectType>>([]);

  const [isFirstRender, setIsFirstRender] = useState(true);
  const simpleSelectOptions = getTranslatedOptions([
    { translateValue: 'all', selectValue: '0' },
    { translateValue: 'zero', selectValue: '0' },
    { translateValue: 'three', selectValue: '3' },
    { translateValue: 'five', selectValue: '5' },
    { translateValue: 'ten', selectValue: '10' },
  ], t, combinedFilterType);
  const [combinedFilterValue, setCombinedFilterValue] = useState<any>(simpleSelectOptions[0]);

  const getSelectParsedOptions = (
    options: ObjectType,
    optionsFromTags: ObjectType,
  ) => options?.map((option: ObjectType) => {
    let selected = false;
    if (optionsFromTags && Array.isArray(optionsFromTags)) {
      optionsFromTags.forEach((optionFromTag: ObjectType) => {
        if (optionFromTag.name === option.name) {
          selected = true;
        }
      });
    }
    return ({ ...option, selected });
  });

  const getOptionsFromTags = async () => {
    const optionsFromTags = initialTags[filterType];
    const selectOpt = SELECT_OPTIONS_BY_FILTER[filterType];
    let options = SELECT_OPTIONS_BY_FILTER[filterType].values;
    if (
      selectOpt.type === 'autocomplete'
      && selectOptions.length === 0
    ) {
      options = options.concat(await getAutocompleteValues(filterType));
      options = getTranslatedOptions(options, t, filterType);
      options = getSelectParsedOptions(options, optionsFromTags);
      return setSelectOptions(options);
    }
    options = selectOptions;
    options = getSelectParsedOptions(options, optionsFromTags);
    return setSelectOptions(options);
  };

  const getInfoToDispatch = (
    optionValues: Array<string | undefined>,
    selectOptionsToChange: ObjectType,
  ) => {
    const updatedFilter = selectOptionsToChange.map(
      (option: ObjectType) => (optionValues.includes(option.name)
        ? { ...option, selected: true }
        : { ...option, selected: false }),
    );
    const filterSelected = updatedFilter.filter(
      (option: ObjectType) => option.selected === true && ({ ...option }),
    );
    setSelectedOptions(optionValues);
    setTags(filterSelected);
    const filtersToStore = {
      filters: filterSelected,
      combinedFilterValue: combinedFilterValue.selectValue,
    };
    const filtersParsed = getUrlCombinedFilterParamsToStore(
      filtersToStore,
      filterType,
      event.ADD,
    );
    const filterToUpdate = {
      [filterType]: filtersParsed,
    };
    setSelectOptions(updatedFilter);
    return { filterToUpdate, filterSelected };
  };

  const dispatchMultiSelect = (filterInfoToDispatch: ObjectType) => {
    const { filterToUpdate, filterSelected } = filterInfoToDispatch;
    dispatch(toggleIsLoading(LIST_TYPES.civilWorksWorkers));
    dispatch(updateListsByFilter(filterToUpdate, { [filterType]: filterSelected }));
  };

  const debounceMultiSelect = useCallback(
    debounce(dispatchMultiSelect, 700),
    [],
  );

  useEffect(() => {
    const tagsToSet = initialTags[filterType] || [];
    setTags(tagsToSet);
    getOptionsFromTags();
  }, [initialTags]);

  const getFiltersToUpdate = (currentTags: ObjectType[]) => {
    const filtersFromOptions = currentTags
      .filter((filter: ObjectType) => (filter && filter.name) && filter);

    const filtersToStore = {
      filters: filtersFromOptions,
      combinedFilterValue: combinedFilterValue.selectValue,
    };
    const filtersParsed = getUrlCombinedFilterParamsToStore(
      filtersToStore,
      filterType,
      event.ADD,
    );
    const filterToUpdate = {
      [filterType]: filtersParsed,
    };
    return { filterToUpdate, filtersFromOptions };
  };

  const dispatchSimpleSelect = (filterInfoToDispatch: ObjectType) => {
    const { filterToUpdate, filtersFromOptions } = filterInfoToDispatch;
    dispatch(toggleIsLoading(LIST_TYPES.civilWorksWorkers));
    dispatch(updateListsByFilter(filterToUpdate, { [filterType]: filtersFromOptions }));
  };

  const debounceSimpleSelect = useCallback(
    debounce(dispatchSimpleSelect, 600),
    [],
  );

  useEffect(() => {
    if (!isFirstRender) {
      const filterInfoToDispatch = getFiltersToUpdate(tags);
      debounceSimpleSelect(filterInfoToDispatch);
    } else {
      setIsFirstRender(false);
    }
  }, [combinedFilterValue]);

  const firstChildComponent = (
    <CustomSelectMultiple
      filterType={filterType}
      customStyle={{ paddingTop: '0.75rem' }}
      selectOptions={selectOptions}
      selectedOptions={selectedOptions}
      setSelectedOptions={setSelectedOptions}
      getInfoToDispatch={getInfoToDispatch}
      debounceSelectedInfo={debounceMultiSelect}
      defaultValue={t(`filtersPlaceHolder.${filterType}`)}
      required={false}
    />
  );

  const secondChildComponent = (
    <CustomSelect
      customStyle={{ paddingTop: '0.75rem' }}
      selectValue={combinedFilterValue.name}
      selectOptions={simpleSelectOptions}
      handleSelectChange={(e: any) => setCombinedFilterValue(simpleSelectOptions
        .find((opt) => opt.name === e.target.value))}
      errorMessage={''}
      disabled={tags.length === 0}
      disableSelecOptionEmpty={true}
    />
  );

  return (
    <CombinedFilterCollapsableWrapper
      filterType={filterType}
      tags={tags}
      firstChild={firstChildComponent}
      secondChild={secondChildComponent}
      setTags={setTags}
      combinedFilterValue={combinedFilterValue}
      tooltipMessage={tooltipMessage}
    />
  );
};
